import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Minus, Pencil, Plus, ShoppingBag, Sprout, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { buildCartMessage, openWhatsApp } from "../lib/site";

const EMPTY_FORM = {
  fullName: "", mobile: "", address: "", city: "", state: "", pincode: "",
  landmark: "", altMobile: "", instructions: "",
};

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

const validateForm = (f) => {
  const errors = {};
  if (!f.fullName.trim()) errors.fullName = "Please enter your full name.";
  if (!INDIAN_MOBILE.test(f.mobile)) errors.mobile = "Enter a valid 10-digit Indian mobile number (starts with 6, 7, 8 or 9).";
  if (!f.address.trim()) errors.address = "Please enter your complete delivery address.";
  if (!f.city.trim()) errors.city = "Please enter your city.";
  if (!f.state.trim()) errors.state = "Please enter your state.";
  if (!/^\d{6}$/.test(f.pincode)) errors.pincode = "Enter a valid 6-digit PIN code.";
  if (f.altMobile && !INDIAN_MOBILE.test(f.altMobile)) errors.altMobile = "Enter a valid 10-digit mobile number, or leave this blank.";
  return errors;
};

const onlyDigits = (value, max) => value.replace(/\D/g, "").slice(0, max);

const STEPS = [
  ["cart", "Cart"],
  ["details", "Details"],
  ["review", "Review"],
];

export default function CartDrawer() {
  const { items, count, updateQty, remove, open, setOpen } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const bodyRef = useRef(null);

  // If the cart is emptied while on details/review, drop back to the cart step
  useEffect(() => {
    if (items.length === 0 && step !== "cart") setStep("cart");
  }, [items.length, step]);

  // Reset scroll position whenever the step changes
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const goToDetails = () => {
    if (items.length === 0) return;
    setStep("details");
  };

  const goToReview = () => {
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStep("review");
  };

  const sendOnWhatsApp = () => {
    if (items.length === 0) return;
    openWhatsApp(buildCartMessage(items, form));
  };

  const fieldProps = (key) => ({
    className: `field-input${errors[key] ? " field-invalid" : ""}`,
    value: form[key],
    onChange: (e) => setField(key, e.target.value),
    "aria-invalid": Boolean(errors[key]),
  });

  const stepIndex = STEPS.findIndex(([id]) => id === step);

  const headTitle = {
    cart: <>Cart <em>·</em> {count} item{count === 1 ? "" : "s"}</>,
    details: <>Delivery <em>Details</em></>,
    review: <>Review <em>Order</em></>,
  }[step];

  return (
    <>
      <div className={open ? "cart-scrim is-open" : "cart-scrim"} onClick={() => setOpen(false)} aria-hidden={!open} />
      <AnimatePresence>
        {open && (
          <motion.aside className="cart-drawer" aria-label="Shopping cart" data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}>
            <div className="cart-head">
              <div>
                <p className="eyebrow"><span /> {step === "cart" ? "Your basket" : `Step ${stepIndex + 1} of 3`}</p>
                <h3>{headTitle}</h3>
                {items.length > 0 && (
                  <div className="cart-steps" data-testid="cart-steps">
                    {STEPS.map(([id, label], i) => (
                      <span key={id} className={`cart-step${i === stepIndex ? " active" : ""}${i < stepIndex ? " done" : ""}`}
                        data-testid={`cart-step-${id}`}>{label}</span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setOpen(false)} data-testid="cart-close-button" aria-label="Close cart"><X size={20} /></button>
            </div>

            <div className="cart-body" ref={bodyRef}>
              {items.length === 0 ? (
                <div className="cart-empty" data-testid="cart-empty-state">
                  <Sprout size={28} />
                  <p>Your basket is empty.</p>
                  <span>Add a product to get started.</span>
                  <Link to="/shop" className="cart-continue-empty" onClick={() => setOpen(false)}>Browse Shop All →</Link>
                </div>
              ) : step === "cart" ? (
                <ul className="cart-list cart-list-simple" data-testid="cart-step-cart">
                  <AnimatePresence initial={false}>
                    {items.map((item) => (
                      <motion.li key={item.id} data-testid={`cart-item-${item.id}`}
                        initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
                        transition={{ duration: 0.35 }} layout>
                        <div className="cart-item-info">
                          <p className="cart-item-title">{item.name}</p>
                          <span>{item.size}</span>
                        </div>
                        <div className="cart-item-controls">
                          <div className="qty-stepper">
                            <button onClick={() => updateQty(item.id, -1)} data-testid={`cart-decrease-${item.id}`} aria-label="Decrease"><Minus size={14} /></button>
                            <span data-testid={`cart-quantity-${item.id}`}>{item.quantity}</span>
                            <button onClick={() => updateQty(item.id, 1)} data-testid={`cart-increase-${item.id}`} aria-label="Increase"><Plus size={14} /></button>
                          </div>
                          <button className="cart-remove" onClick={() => remove(item.id)} data-testid={`cart-remove-${item.id}`} aria-label="Remove"><Trash2 size={14} /></button>
                        </div>
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
              ) : step === "details" ? (
                <form className="cust-form" data-testid="customer-details-form" noValidate
                  onSubmit={(e) => { e.preventDefault(); goToReview(); }}>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-fullname">Full Name <span className="req">*</span></label>
                    <input id="cust-fullname" data-testid="details-fullname-input" placeholder="e.g. Ramesh Kumar" autoComplete="name" {...fieldProps("fullName")} />
                    {errors.fullName && <p className="field-error" data-testid="details-error-fullname">{errors.fullName}</p>}
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-mobile">Mobile Number <span className="req">*</span></label>
                    <input id="cust-mobile" data-testid="details-mobile-input" placeholder="10-digit mobile number" inputMode="numeric" autoComplete="tel-national"
                      {...fieldProps("mobile")} onChange={(e) => setField("mobile", onlyDigits(e.target.value, 10))} />
                    {errors.mobile && <p className="field-error" data-testid="details-error-mobile">{errors.mobile}</p>}
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-address">Complete Delivery Address <span className="req">*</span></label>
                    <textarea id="cust-address" data-testid="details-address-input" placeholder="House / Flat No., Street, Area, Locality" autoComplete="street-address" {...fieldProps("address")} />
                    {errors.address && <p className="field-error" data-testid="details-error-address">{errors.address}</p>}
                  </div>
                  <div className="field-row">
                    <div className="field-group">
                      <label className="field-label" htmlFor="cust-city">City <span className="req">*</span></label>
                      <input id="cust-city" data-testid="details-city-input" placeholder="e.g. Jaipur" autoComplete="address-level2" {...fieldProps("city")} />
                      {errors.city && <p className="field-error" data-testid="details-error-city">{errors.city}</p>}
                    </div>
                    <div className="field-group">
                      <label className="field-label" htmlFor="cust-state">State <span className="req">*</span></label>
                      <input id="cust-state" data-testid="details-state-input" placeholder="e.g. Rajasthan" autoComplete="address-level1" {...fieldProps("state")} />
                      {errors.state && <p className="field-error" data-testid="details-error-state">{errors.state}</p>}
                    </div>
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-pincode">PIN Code <span className="req">*</span></label>
                    <input id="cust-pincode" data-testid="details-pincode-input" placeholder="6-digit PIN code" inputMode="numeric" autoComplete="postal-code"
                      {...fieldProps("pincode")} onChange={(e) => setField("pincode", onlyDigits(e.target.value, 6))} />
                    {errors.pincode && <p className="field-error" data-testid="details-error-pincode">{errors.pincode}</p>}
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-landmark">Landmark <span className="opt">(optional)</span></label>
                    <input id="cust-landmark" data-testid="details-landmark-input" placeholder="e.g. Near City Mall" {...fieldProps("landmark")} />
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-altmobile">Alternate Mobile Number <span className="opt">(optional)</span></label>
                    <input id="cust-altmobile" data-testid="details-altmobile-input" placeholder="10-digit alternate number" inputMode="numeric"
                      {...fieldProps("altMobile")} onChange={(e) => setField("altMobile", onlyDigits(e.target.value, 10))} />
                    {errors.altMobile && <p className="field-error" data-testid="details-error-altmobile">{errors.altMobile}</p>}
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="cust-instructions">Delivery Instructions <span className="opt">(optional)</span></label>
                    <textarea id="cust-instructions" data-testid="details-instructions-input" placeholder="e.g. Call before delivery, leave at gate" {...fieldProps("instructions")} />
                  </div>
                </form>
              ) : (
                <div data-testid="order-review">
                  <div className="review-section">
                    <h4>Order Items</h4>
                    <ul className="review-items" data-testid="review-items">
                      {items.map((item) => (
                        <li key={item.id} data-testid={`review-item-${item.id}`}>
                          <strong>{item.name}</strong>
                          <span>{item.size} · Qty {item.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="review-section">
                    <h4>Delivery Details</h4>
                    <dl className="review-detail" data-testid="review-details">
                      <dt>Full Name</dt><dd data-testid="review-fullname">{form.fullName}</dd>
                      <dt>Mobile</dt><dd data-testid="review-mobile">{form.mobile}</dd>
                      <dt>Address</dt><dd data-testid="review-address">{form.address}</dd>
                      <dt>City</dt><dd data-testid="review-city">{form.city}</dd>
                      <dt>State</dt><dd data-testid="review-state">{form.state}</dd>
                      <dt>PIN Code</dt><dd data-testid="review-pincode">{form.pincode}</dd>
                      {form.landmark && (<><dt>Landmark</dt><dd data-testid="review-landmark">{form.landmark}</dd></>)}
                      {form.altMobile && (<><dt>Alternate</dt><dd data-testid="review-altmobile">{form.altMobile}</dd></>)}
                      {form.instructions && (<><dt>Instructions</dt><dd data-testid="review-instructions">{form.instructions}</dd></>)}
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="cart-foot">
                {step === "cart" && (
                  <>
                    <p className="cart-pricing-note">
                      Prices, shipping and final total will be confirmed on WhatsApp.
                    </p>
                    <button className="cart-btn cart-btn-solid cart-btn-full" onClick={goToDetails} data-testid="cart-confirm-whatsapp">
                      <MessageCircle size={18} /> Confirm Order on WhatsApp
                    </button>
                    <Link to="/shop" onClick={() => setOpen(false)} className="cart-btn cart-btn-outline cart-btn-full" data-testid="cart-continue-shopping">
                      <ShoppingBag size={16} /> Continue Shopping
                    </Link>
                  </>
                )}
                {step === "details" && (
                  <>
                    <button className="cart-btn cart-btn-solid cart-btn-full" onClick={goToReview} data-testid="details-continue-button">
                      Review Order →
                    </button>
                    <button className="cart-btn cart-btn-outline cart-btn-full" onClick={() => setStep("cart")} data-testid="details-back-button">
                      <ArrowLeft size={16} /> Back to Cart
                    </button>
                  </>
                )}
                {step === "review" && (
                  <>
                    <p className="cart-pricing-note">
                      Your order is placed only after you send the message on WhatsApp. Prices, shipping and final amount will be confirmed there.
                    </p>
                    <button className="cart-btn cart-btn-solid cart-btn-full" onClick={sendOnWhatsApp} data-testid="review-send-whatsapp-button">
                      <MessageCircle size={18} /> Send Order on WhatsApp
                    </button>
                    <div className="cart-actions">
                      <button className="cart-btn cart-btn-outline" onClick={() => setStep("details")} data-testid="review-edit-details-button">
                        <Pencil size={14} /> Edit Details
                      </button>
                      <button className="cart-btn cart-btn-outline" onClick={() => setStep("cart")} data-testid="review-edit-cart-button">
                        <ShoppingBag size={14} /> Edit Cart
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
